import React from 'react';
import type { FieldErrors, UseFormRegister } from 'react-hook-form';
import type { InventoryItemFormData } from '../../../types/inventory';
import { useLanguage } from '../../../features/language/useLanguage';

interface InventoryBasicInfoSectionProps {
  register: UseFormRegister<InventoryItemFormData>;
  errors: FieldErrors<InventoryItemFormData>;
}

export const InventoryBasicInfoSection: React.FC<InventoryBasicInfoSectionProps> = ({
  register,
  errors,
}) => {
  const { t } = useLanguage();
  return (
    <div className="bg-white shadow rounded-lg p-6 space-y-6" data-test-id="inventory-basic-info">
      <h2 className="text-lg font-medium text-gray-900 border-b pb-2">{t('inventory.basicInfo.title')}</h2>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-gray-700">{t('inventory.modal.fields.code')}</label>
          <input
            type="text"
            {...register('code')}
            disabled
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2 bg-gray-100 text-gray-500"
            data-test-id="inventory-create-code"
            placeholder={t('inventory.basicInfo.generatedBySystem')}
          />
          {errors.code && <p className="mt-1 text-xs text-red-600">{errors.code.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">{t('forms.customer.name')}</label>
          <input
            type="text"
            {...register('name')}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
            data-test-id="inventory-create-name"
          />
          {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name.message}</p>}
        </div>

        <div className="sm:col-span-2">
          <label className="block text-sm font-medium text-gray-700">{t('inventory.modal.fields.description')}</label>
          <textarea
            {...register('description')}
            rows={3}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
            data-test-id="inventory-create-description"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">{t('inventory.modal.fields.itemType')}</label>
          <select
            {...register('itemType')}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
            data-test-id="inventory-create-type"
          >
            <option value="green_bean">{t('common.terms.greenBean')}</option>
            <option value="roasted_bulk">{t('common.terms.roastedBulk')}</option>
            <option value="finished_good">{t('common.terms.finishedGood')}</option>
          </select>
          {errors.itemType && <p className="mt-1 text-xs text-red-600">{errors.itemType.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">{t('inventory.basicInfo.unitOfMeasure')}</label>
          <select
            {...register('unitOfMeasure')}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
            data-test-id="inventory-create-uom"
          >
            <option value="kg">{t('inventory.basicInfo.units.kg')}</option>
            <option value="g">{t('inventory.basicInfo.units.g')}</option>
            <option value="lb">{t('inventory.basicInfo.units.lb')}</option>
            <option value="oz">{t('inventory.basicInfo.units.oz')}</option>
            <option value="pcs">{t('inventory.basicInfo.units.pcs')}</option>
            <option value="bag">{t('inventory.basicInfo.units.bag')}</option>
          </select>
          {errors.unitOfMeasure && <p className="mt-1 text-xs text-red-600">{errors.unitOfMeasure.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">{t('inventory.modal.fields.location')}</label>
          <input
            type="text"
            {...register('location')}
            placeholder={t('inventory.modal.fields.locationPlaceholder')}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
            data-test-id="inventory-create-location"
          />
        </div>
      </div>
    </div>
  );
};
